import { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Switch,
  Button,
  Chip,
  Box,
  IconButton,
  Collapse,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  HourglassEmpty as HourglassIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import api from '../../services/api';
import ActionsList from './ActionsList';
import { workingPulseSx } from '../../theme';
import type { AutoCommenterProgress } from '../../hooks/useAutoCommenterWebSocket';
import ScheduleBar from '../common/ScheduleBar';
import { useWaitingCountdown } from '../../utils/schedule';

interface ConfigCardProps {
  config: {
    id: number;
    name: string;
    keywords: string[];
    templates: string[];
    templateMode: string;
    maxCommentsPerDay: number;
    todayComments: number;
    totalComments: number;
    isActive: boolean;
    lastRunAt?: string | null;
    account: {
      firstName: string;
      lastName: string;
    };
  };
  onUpdate: () => void;
  onDelete: (id: number) => void;
  progress?: AutoCommenterProgress | null;
}

const getSkipReasonText = (reason: string): string => {
  switch (reason) {
    case 'already_commented': return 'Уже прокомментировано';
    case 'daily_limit': return 'Лимит достигнут';
    case 'account_limit': return 'Лимит аккаунта';
    case 'error': return 'Ошибка';
    default: return reason;
  }
};

const getPhaseIcon = (phase: AutoCommenterProgress['phase']) => {
  switch (phase) {
    case 'searching': return <SearchIcon fontSize="small" />;
    case 'processing': return <SettingsIcon fontSize="small" />;
    case 'waiting': return <HourglassIcon fontSize="small" />;
    case 'completed': return <CheckCircleIcon fontSize="small" />;
    case 'error': return <ErrorIcon fontSize="small" />;
  }
};

const getPhaseColor = (phase: AutoCommenterProgress['phase']): string => {
  switch (phase) {
    case 'searching': return 'info.main';
    case 'processing': return 'primary.main';
    case 'waiting': return 'warning.main';
    case 'completed': return 'success.main';
    case 'error': return 'error.main';
  }
};

export default function ConfigCard({ config, onUpdate, onDelete, progress }: ConfigCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  // Shared countdown + working state — same UX as auto-liker
  const { secLeft: waitSecLeft, pct: waitProgressPct, totalSec: waitTotalSec } =
    useWaitingCountdown(progress);
  const isWorking =
    !!progress && progress.phase !== 'completed' && progress.phase !== 'error';

  const handleRunNow = async () => {
    setRunningNow(true);
    try {
      const res = await api.post(`/auto-commenter/${config.id}/run`);
      const { queued } = res.data.data || {};
      if (queued) toast.success('Запущено — следите за прогрессом');
      else toast(res.data.message || 'Не запущено', { icon: '⚠️' });
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось запустить');
    } finally {
      setRunningNow(false);
    }
  };

  const handleToggle = async () => {
    setIsToggling(true);
    try {
      await api.post(`/auto-commenter/${config.id}/toggle`);
      toast.success(config.isActive ? 'Правило выключено' : 'Правило включено');
      onUpdate();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось переключить правило');
    } finally {
      setIsToggling(false);
    }
  };

  // Delete confirm via MUI dialog (consistent with auto-liker — no native confirm())
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/auto-commenter/${config.id}`);
      toast.success('Правило удалено');
      onDelete(config.id);
      setDeleteOpen(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось удалить');
    } finally {
      setDeleting(false);
    }
  };

  const dailyProgress = (config.todayComments / config.maxCommentsPerDay) * 100;

  return (
    <Card
      sx={{
        mb: 2,
        borderColor: isWorking ? 'secondary.main' : undefined,
        transition: 'border-color 0.3s, box-shadow 0.3s',
        ...(isWorking ? workingPulseSx : {}),
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="h6" gutterBottom>
              {config.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Аккаунт: {config.account.firstName} {config.account.lastName}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Ключевые слова:</Typography>
              <Box sx={{ mt: 0.5 }}>
                {config.keywords.map((kw, i) => (
                  <Chip key={i} label={kw} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                ))}
              </Box>
            </Box>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Шаблоны ({config.templateMode === 'random' ? 'случайно' : 'по очереди'}):
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {config.templates.slice(0, 3).map((tpl, i) => (
                  <Chip
                    key={i}
                    label={tpl.length > 30 ? tpl.substring(0, 30) + '...' : tpl}
                    size="small"
                    variant="outlined"
                    color="secondary"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))}
                {config.templates.length > 3 && (
                  <Chip label={`+${config.templates.length - 3}`} size="small" variant="outlined" />
                )}
              </Box>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box textAlign="right">
              <Typography variant="body2" color="text.secondary">
                Сегодня: {config.todayComments} / {config.maxCommentsPerDay}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Всего: {config.totalComments}
              </Typography>
              <Box
                sx={{
                  width: 100,
                  height: 6,
                  bgcolor: 'grey.300',
                  borderRadius: 1,
                  mt: 0.5,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${Math.min(dailyProgress, 100)}%`,
                    height: '100%',
                    bgcolor: dailyProgress >= 100 ? 'error.main' : 'secondary.main',
                    transition: 'width 0.3s',
                  }}
                />
              </Box>
            </Box>
            <Switch
              checked={config.isActive}
              onChange={handleToggle}
              disabled={isToggling}
              color="secondary"
            />
          </Box>
        </Box>

        <ScheduleBar
          isActive={config.isActive}
          lastRunAt={config.lastRunAt}
          isWorking={isWorking}
          runningNow={runningNow}
          onRunNow={handleRunNow}
        />

        {/* Status Indicator */}
        {progress && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
              border: 1,
              borderColor: getPhaseColor(progress.phase),
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={progress.details?.postsFound ? 1 : 0}>
              <Box sx={{ color: getPhaseColor(progress.phase) }}>
                {getPhaseIcon(progress.phase)}
              </Box>
              <Typography variant="body2" sx={{ color: getPhaseColor(progress.phase) }}>
                {waitSecLeft !== null
                  ? `Ожидание следующего действия: ${waitSecLeft} сек…`
                  : progress.message}
              </Typography>
            </Box>

            {/* Countdown (waiting phase) */}
            {waitSecLeft !== null && waitTotalSec > 0 && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={waitProgressPct}
                  color="warning"
                  sx={{ height: 6, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {waitSecLeft} из {waitTotalSec} сек
                </Typography>
              </Box>
            )}

            {/* Progress bar for processing phase */}
            {progress.phase === 'processing' && progress.details?.postsFound && progress.details.postsFound > 0 && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={((progress.details.postsProcessed || 0) / progress.details.postsFound) * 100}
                  sx={{ height: 6, borderRadius: 1 }}
                  color="secondary"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {progress.details.postsProcessed || 0} / {progress.details.postsFound}
                </Typography>
              </Box>
            )}

            {/* Skipped reasons */}
            {progress.details?.skippedReasons && progress.details.skippedReasons.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {progress.details.skippedReasons.map((r, i) => (
                  <Chip
                    key={i}
                    label={`${getSkipReasonText(r.reason)}: ${r.count}`}
                    size="small"
                    variant="outlined"
                    color={r.reason === 'error' ? 'error' : 'default'}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Box>
          <Button
            size="small"
            startIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Скрыть' : 'Показать'} историю
          </Button>
        </Box>
        <IconButton color="error" size="small" onClick={() => setDeleteOpen(true)}>
          <DeleteIcon />
        </IconButton>
      </CardActions>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ pt: 0 }}>
          <ActionsList configId={config.id} />
        </CardContent>
      </Collapse>

      {/* ===== Delete confirm ===== */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Удалить правило?
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography>
              Будет удалено правило <b>«{config.name}»</b> и вся история его действий.
            </Typography>
            <Alert severity="warning">
              VK-аккаунт и накопленные счётчики аккаунта останутся — удаляется только это правило
              автокомментатора.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Удаляю…' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
